import { db } from "./Dexie-Setup";
import { generateCompletePdfBase64 } from "./PDF-Generator";

/**
 * Submits a Daily Field Report locally in a single, atomic transaction.
 * This function is the single source of truth for the submission process.
 * It fetches all required data, generates the PDF, and updates the record.
 * If any step fails, the entire transaction is rolled back by Dexie.
 *
 * @param {number} recordId The primary key of the dailyFieldReports record to submit.
 * @returns {Promise<boolean>} A promise that resolves to true if the submission was successful, false otherwise.
 */
export const submitDfrLocally = async (recordId) => {
  if (typeof recordId !== "number") {
    console.error("submitDfrLocally requires a numeric recordId.");
    return false;
  }

  try {
    // HACK:
    // --- START: Operations moved outside transaction to prevent TransactionInactiveError ---
    // The following operations (fetching report and generating PDF) are performed outside
    // the main Dexie transaction. This is a workaround to prevent a 'TransactionInactiveError'
    // that occurred when complex asynchronous operations, particularly those involving
    // external libraries like @react-pdf/renderer, were executed within the transaction.
    // Moving them out ensures the transaction remains short and focused solely on the
    // database update, which resolves the issue.
    const report = await db.dailyFieldReports.get(recordId);
    if (!report) {
      throw new Error(`DFR with recordId ${recordId} not found.`);
    }

    // Query the job to check the ticket count
    let ticketCount = 0;
    if (
      report.jobNumber &&
      (typeof report.jobNumber === "string" ||
        typeof report.jobNumber === "number" ||
        report.jobNumber instanceof Date || Array.isArray(report.jobNumber))
    ) {
      ticketCount = await db.dailyFieldReports
        .where({ jobNumber: report.jobNumber })
        .count();
    }

    // Set the ticket number
    const ticketNumStamp = ticketCount; // NOTE: If there is no ticket for the job, the ticket number will be 1

    const pdfBase64 = await generateCompletePdfBase64({
      ...report,
      ticketNumStamp,
    }, db); // NOTE: Passing 'db' instead of 'tx' here.
    if (!pdfBase64) {
      throw new Error("PDF generation failed.");
    }
    const pdfData = {
      fileName: `${report.jobNumber}-Ticket.pdf`,
      data: `data:application/pdf;base64,${pdfBase64}`,
    };
    // --- END: Operations moved outside transaction ---

    // Dexie's transaction will automatically handle rollback on any error.
    await db.transaction(
      "rw",
      db.dailyFieldReports,
      db.equipmentLineItems,
      db.laborLineItems,
      db.jobs,
      async (tx) => {
        // This update is now the primary operation within the transaction.
        await tx.dailyFieldReports.update(recordId, {
          ticketNumStamp,
          submitted: true,
          locallyUpdatedAt: Date.now(),
          pdf: pdfData,
        });
      },
    );

    return true;
  } catch (error) {
    console.error(`Failed to submit DFR ${recordId}:`, error);
    return false;
  }
};

